import { en } from './messages/en.ts'
import { ko } from './messages/ko.ts'

/** 사전의 키 집합. 사전에 없는 키는 컴파일 단계에서 걸린다 — 런타임 누락 문구가 생기지 않는다. */
export type MessageKey = keyof typeof ko

/** 로케일 한 벌. 기본 로케일(ko)만 전수를 채우고, 나머지는 채운 만큼만 이긴다. */
export type Messages = Record<MessageKey, string>

/**
 * 사전이 있는 로케일. 통합 콘솔만 영어를 고를 수 있다(eric 확정 2026-11-02 「일단 통합만」).
 * 빠뜨린 키는 자동으로 `ko` 로 떨어진다(아래 fallback).
 */
export type Locale = 'ko' | 'en'

export const DEFAULT_LOCALE: Locale = 'ko'

const MESSAGES: Record<Locale, Partial<Messages>> = { ko, en }

export function isLocale(value: unknown): value is Locale {
  return value === 'ko' || value === 'en'
}

let currentLocale: Locale = DEFAULT_LOCALE

/**
 * 이 앱이 고르게 하는 언어 — **앱이 정한다**(`initLocale` · `@ctcells/core`). 안 정한 앱(모듈 3종 · 장비 관점)은 한국어뿐이고
 * 설정 화면의 「언어」 줄도 고를 수 없다. 설정 화면은 이 목록만 그린다 — 화면 코드에 앱 이름 분기를 두지 마라.
 */
let supportedLocales: readonly Locale[] = [DEFAULT_LOCALE]

export function getSupportedLocales(): readonly Locale[] {
  return supportedLocales
}

export function setSupportedLocales(locales: readonly Locale[]): void {
  supportedLocales = locales.length === 0 ? [DEFAULT_LOCALE] : locales
}

export function getLocale(): Locale {
  return currentLocale
}

/**
 * 로케일을 바꾼다. 부팅 때 **화면 모듈이 평가되기 전에** 한 번 부른다(`apps/console/src/bootLocale.ts`).
 * 🔴 런타임 전환이 아니다 — `t()` 는 순수 함수라 다시 그려지지 않는다. 언어 전환은 저장 후 새로 고침이다(`changeLocale` · docs/i18n.md).
 */
export function setLocale(locale: Locale): void {
  currentLocale = locale
}

/** `{name}` 자리에 값을 끼운다. 넘기지 않은 자리는 건드리지 않는다. */
function interpolate(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = vars[name]
    return value === undefined ? whole : String(value)
  })
}

/**
 * 화면 문구를 가져온다. 문자열 리터럴을 화면 코드에 직접 쓰지 않는다.
 *
 * 🔴 **사전에 없는 키가 와도 `undefined` 를 내지 않는다.** 서명은 `MessageKey` 지만
 *    캐스트 한 줄이면 뚫린다(`snapshot.state as ModuleState`). 실제로 그렇게 뚫려서
 *    `undefined` 가 `string` 인 척 나갔고 **배지 안이 통째로 비었다**(2026-09-20 실물).
 *    빈 화면보다 키가 보이는 편이 낫다 — 보이면 고칠 수 있다.
 *    ⚠ 런타임 값으로 키를 만드는 자리는 `t()` 가 아니라 `enumLabel()` 을 쓴다.
 */
export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const template = MESSAGES[currentLocale][key] ?? ko[key] ?? key
  return vars ? interpolate(template, vars) : template
}

/**
 * **서버로 나가는 글자** — 언어와 상관없이 언제나 한글 사전이다(eric 2026-11-02 「서버는 그대로 놔둬」).
 * 사유 기본값 · 메모처럼 요청 본문에 실려 감사 원장에 남는 글자는 `t()` 를 쓰지 않고 이것을 쓴다. 화면에 띄우는 글자에는 쓰지 마라.
 */
export function serverText(key: MessageKey, vars?: Record<string, string | number>): string {
  const template = ko[key] ?? key
  return vars ? interpolate(template, vars) : template
}

/** 지금 언어의 사전에 «그 언어 글자»가 있는가 — `ko` 로 떨어지는 경우와 가를 때만 쓴다(에러 문구 순서 · `errors.ts`) */
export function hasLocalizedMessage(key: MessageKey): boolean {
  return Object.hasOwn(MESSAGES[currentLocale], key)
}

/** 키가 사전에 있는지 본다. 서버가 준 코드처럼 런타임 값으로 키를 만들 때 쓴다. */
export function hasMessage(key: string): key is MessageKey {
  return key in ko
}
